import { parseDateOnly, parseDateTime, toDateOnly } from '@agroone/dates'

import { AgrooneNutrient } from './agroone-nutrient'
import { AoDocumentDto } from './ao-document.dto'
import { Location, LocationDAO } from './location'
import { MechanicalWeeding } from './mechanical-weeding'
import { NutrientActivity } from './nutrient'
import { Reentry } from './pesticide'
import { Product } from './product'
import {
  RecommendationFoodSafety,
  RecommendationIrrigation,
  RecommendationOtherOperation,
  RecommendationPesticides,
  RecommendationPlanting,
} from './recommendation'
import {
  WorkOrderFoodSafety,
  WorkOrderIrrigation,
  WorkOrderMechanicalWeeding,
  WorkOrderOtherOperation,
  WorkOrderPesticide,
  WorkOrderPlanting,
} from './work-order'

export class InterventionIrrigation {
  id?: number
  completedStartingDate: string | null
  completedEndingDate: string | null
  area: number
  amount: number
  areaUnit: string
  amountUnit: string
  nutrients?: AgrooneNutrient[] | NutrientActivity[]

  get completedStartingDateAsDate(): Date | undefined {
    return parseDateOnly(this.completedStartingDate) ?? undefined
  }

  get completedEndingDateAsDate(): Date | undefined {
    return parseDateOnly(this.completedEndingDate) ?? undefined
  }

  constructor(
    interventionIrrigation?: Omit<InterventionIrrigation, 'completedStartingDateAsDate' | 'completedEndingDateAsDate'>,
  ) {
    this.completedStartingDate = interventionIrrigation?.completedStartingDate ?? toDateOnly(new Date())
    this.completedEndingDate = interventionIrrigation?.completedEndingDate ?? toDateOnly(new Date())
    this.area = interventionIrrigation?.area ?? 0
    this.amount = interventionIrrigation?.amount ?? 0
    this.areaUnit = interventionIrrigation?.areaUnit ?? ''
    this.amountUnit = interventionIrrigation?.amountUnit ?? ''
    this.nutrients = interventionIrrigation?.nutrients
  }
}

export class InterventionMechanicalWeeding {
  id?: number
  area: string | undefined
  areaUnit: string | undefined | null
  completedDate: string

  get completedDateAsDate(): Date | undefined {
    return parseDateOnly(this.completedDate) ?? undefined
  }

  constructor(recommendationMechanicalWeeding?: Omit<InterventionMechanicalWeeding, 'completedDateAsDate'>) {
    this.area = recommendationMechanicalWeeding?.area
    this.areaUnit = recommendationMechanicalWeeding?.areaUnit
    this.completedDate = recommendationMechanicalWeeding?.completedDate ?? ''
  }
}

export class InterventionOtherOperation {
  id?: number
  operationType: string | undefined
  area: string | undefined
  areaUnit: string | undefined | null
  completedDate: string

  get completedDateAsDate(): Date | undefined {
    return parseDateOnly(this.completedDate) ?? undefined
  }

  constructor(recommendationOtherOperation?: Omit<InterventionOtherOperation, 'completedDateAsDate'>) {
    this.operationType = recommendationOtherOperation?.operationType
    this.area = recommendationOtherOperation?.area
    this.areaUnit = recommendationOtherOperation?.areaUnit
    this.completedDate = recommendationOtherOperation?.completedDate ?? ''
  }
}

export class InterventionForeignMaterial {
  id?: number
  type?: string
  subType?: string
  area?: string
  completedDate: string | null

  get completedDateAsDate(): Date | undefined | null {
    return parseDateTime(this.completedDate)
  }

  constructor(interventionForeignMaterial?: Omit<InterventionForeignMaterial, 'completedDateAsDate'>) {
    this.completedDate = interventionForeignMaterial?.completedDate ?? ''
    this.type = interventionForeignMaterial?.type ?? ''
    this.subType = interventionForeignMaterial?.subType ?? ''
    this.area = interventionForeignMaterial?.area ?? ''
  }
}

export class InterventionFoodSafety {
  id?: number
  location?: Location | LocationDAO
  bufferZone?: number
  bufferZoneUnit?: string
  completedDate?: string | null

  get completedDateAsDate(): Date | undefined {
    return parseDateOnly(this.completedDate) ?? undefined
  }

  constructor(interventionFoodSafety?: Omit<InterventionFoodSafety, 'completedDateAsDate'>) {
    this.completedDate = interventionFoodSafety?.completedDate ?? toDateOnly(new Date())
    if (interventionFoodSafety?.location) {
      this.location = interventionFoodSafety?.location
    }
    if (interventionFoodSafety?.bufferZone) {
      this.bufferZone = +interventionFoodSafety?.bufferZone
    }
    if (interventionFoodSafety?.bufferZoneUnit) {
      this.bufferZoneUnit = interventionFoodSafety?.bufferZoneUnit
    }
  }
}

export const FoodSafety = InterventionFoodSafety

export class InterventionPesticidesProduct {
  pesticideId!: string
  targetCropId?: number
  activeIngredientsNames!: string[]
  identificationNumber!: string
  name!: string
  rate!: number
  rateUnit!: string
  rateAreaUnit!: string
  reentries?: Reentry[]
}

export class InterventionPesticide {
  id: number | undefined
  waterAmount: number
  waterUnit: string
  waterAreaUnit: string
  products: InterventionPesticidesProduct[]
  weatherConditions: string | null
  completedDate: string | null
  windSpeed: number | null
  windSpeedUnit: string | null
  applicationEquipment: string | null
  area?: number
  areaUnit?: string
  windDirection?: string | null
  temperature?: number | null
  temperatureUnit?: string | null
  targetPest?: string | null
  supplierId?: number | null
  supplierIsFarm?: boolean | null
  supplierName?: string | null

  constructor(interventionPesticide?: Omit<InterventionPesticide, 'completedDateAsDate'>) {
    this.id = interventionPesticide?.id
    this.waterAmount = interventionPesticide?.waterAmount ?? 0
    this.waterUnit = interventionPesticide?.waterUnit ?? ''
    this.waterAreaUnit = interventionPesticide?.waterAreaUnit ?? ''
    this.products = interventionPesticide?.products ?? []
    this.weatherConditions = interventionPesticide?.weatherConditions ?? ''
    this.completedDate = interventionPesticide?.completedDate ?? ''
    this.windSpeed = interventionPesticide?.windSpeed ?? null
    this.windSpeedUnit = interventionPesticide?.windSpeedUnit ?? null
    this.applicationEquipment = interventionPesticide?.applicationEquipment ?? null
    this.area = interventionPesticide?.area ?? 0
    this.areaUnit = interventionPesticide?.areaUnit ?? ''
    this.windDirection = interventionPesticide?.windDirection ?? null
    this.temperature = interventionPesticide?.temperature ?? null
    this.temperatureUnit = interventionPesticide?.temperatureUnit ?? null
    this.targetPest = interventionPesticide?.targetPest ?? null
    this.supplierId = interventionPesticide?.supplierId ?? null
    this.supplierIsFarm = interventionPesticide?.supplierIsFarm ?? null
    this.supplierName = interventionPesticide?.supplierName ?? null
  }
}

export class InterventionPesticideDAO {
  id?: number
  waterAmount: number
  waterUnit: string
  waterAreaUnit: string
  weatherConditions?: string | null
  completedDate: string
  windSpeed?: number | null
  windSpeedUnit?: string | null
  applicationEquipment?: string | null
  area: number
  areaUnit: string
  windDirection?: string | null
  temperature?: number | null
  temperatureUnit?: string | null
  targetPest?: string | null
  supplierId?: number | null
  supplierIsFarm?: boolean | null
  supplierName?: string | null

  constructor(interventionPesticide: InterventionPesticideDAO) {
    if (interventionPesticide?.id) {
      this.id = interventionPesticide?.id
    }
    this.waterAmount = interventionPesticide?.waterAmount ?? null
    this.waterUnit = interventionPesticide?.waterUnit ?? null
    this.waterAreaUnit = interventionPesticide?.waterAreaUnit ?? null
    this.weatherConditions = interventionPesticide?.weatherConditions ?? null
    this.completedDate = interventionPesticide?.completedDate ?? null
    this.windSpeed = interventionPesticide?.windSpeed ?? null
    this.windSpeedUnit = interventionPesticide?.windSpeedUnit ?? null
    this.applicationEquipment = interventionPesticide?.applicationEquipment ?? null
    this.area = interventionPesticide?.area ?? 0
    this.areaUnit = interventionPesticide?.areaUnit ?? ''
    this.windDirection = interventionPesticide?.windDirection ?? null
    this.temperature = interventionPesticide?.temperature ?? null
    this.temperatureUnit = interventionPesticide?.temperatureUnit ?? null
    this.targetPest = interventionPesticide?.targetPest ?? null
    this.supplierId = interventionPesticide?.supplierId ?? null
    this.supplierIsFarm = interventionPesticide?.supplierIsFarm ?? null
    this.supplierName = interventionPesticide?.supplierName ?? null
  }
}

export class InterventionPesticideProductsDAO {
  id?: number
  rate: number
  rateUnit: string
  rateAreaUnit: string
  interventionPesticideId: number
  pesticideProductId: number
  targetCropId?: number | null

  constructor(interventionPesticide: InterventionPesticideProductsDAO) {
    if (interventionPesticide?.id) {
      this.id = interventionPesticide?.id
    }
    this.rate = interventionPesticide?.rate ?? null
    this.rateUnit = interventionPesticide?.rateUnit ?? null
    this.rateAreaUnit = interventionPesticide?.rateAreaUnit ?? null
    this.interventionPesticideId = interventionPesticide?.interventionPesticideId ?? null
    this.pesticideProductId = interventionPesticide?.pesticideProductId ?? null
    this.targetCropId = interventionPesticide?.targetCropId ?? null
  }
}

export class InterventionPesticidesReentriesDAO {
  id?: number
  reentryInterval: number | undefined
  reentryUnit: string | undefined
  interventionMinValue: number | undefined
  interventionMinUnit: string | undefined
  applicationNumber: number | undefined
  fieldType: string | undefined

  constructor(interventionPesticideReentries?: InterventionPesticidesReentriesDAO) {
    if (interventionPesticideReentries?.id) {
      this.id = interventionPesticideReentries?.id
    }
    this.reentryInterval = interventionPesticideReentries?.reentryInterval
    this.reentryUnit = interventionPesticideReentries?.reentryUnit
    this.interventionMinUnit = interventionPesticideReentries?.interventionMinUnit
    this.interventionMinValue = interventionPesticideReentries?.interventionMinValue
    this.applicationNumber = interventionPesticideReentries?.applicationNumber
    this.fieldType = interventionPesticideReentries?.fieldType
  }
}

export class InterventionRecommendation {
  id!: number
  planting?: RecommendationPlanting
  irrigation?: RecommendationIrrigation
  mechanicalWeeding?: MechanicalWeeding
  otherOperation?: RecommendationOtherOperation
  foodSafety?: RecommendationFoodSafety
  pesticides?: RecommendationPesticides
}

export class InterventionCrop {
  id!: number
  name!: string
  shortName!: string
  isVirtual?: boolean
}

export class InterventionWorkOrder {
  id!: number
  planting?: WorkOrderPlanting
  irrigation?: WorkOrderIrrigation
  mechanicalWeeding?: WorkOrderMechanicalWeeding
  otherOperation?: WorkOrderOtherOperation
  foodSafety?: WorkOrderFoodSafety
  pesticides?: WorkOrderPesticide
}

export class InterventionNutrient {
  id?: number
  targetDate: string | null
  area: number
  areaUnit: string
  amount: number
  amountUnit: string
  nutrients?: AgrooneNutrient[] | NutrientActivity[]
  nameProduct?: string

  get targetDateAsDate(): Date | undefined {
    return parseDateOnly(this.targetDate) ?? undefined
  }

  constructor(interventionNutrient?: Omit<InterventionNutrient, 'targetDateAsDate'>) {
    this.targetDate = interventionNutrient?.targetDate ?? toDateOnly(new Date())

    this.area = interventionNutrient?.area ?? 0
    this.areaUnit = interventionNutrient?.areaUnit ?? ''
    this.amount = interventionNutrient?.amount ?? 0
    this.amountUnit = interventionNutrient?.amountUnit ?? ''
    this.nutrients = interventionNutrient?.nutrients
  }
}

export class Intervention {
  static readonly className: string = 'Intervention'

  id: number | undefined
  completedBy: number | undefined | null
  completedByName: string | undefined
  createdByName: string | undefined
  date: string | undefined | null
  crops: InterventionCrop[]
  type: string | undefined
  subType: string | undefined
  region: string | undefined
  regionId: number | null
  note?: string | null
  picturesPath?: string[]
  recommendation?: InterventionRecommendation
  workOrder?: InterventionWorkOrder
  nutrient?: InterventionNutrient
  foodSafety?: InterventionFoodSafety
  mechanicalWeeding?: InterventionMechanicalWeeding
  otherOperation?: InterventionOtherOperation
  pesticides?: InterventionPesticide | InterventionPesticides
  irrigation?: InterventionIrrigation
  foreignMaterial?: InterventionForeignMaterial
  disablingDate?: string | null
  externalId?: number
  externalClientId?: number
  documents?: Partial<AoDocumentDto>[] | null
  validatedDate?: string | null
  validatedBy?: number | null
  validatedByFirstname?: string | null
  validatedByLastname?: string | null

  get dateAsDate(): Date | undefined {
    return parseDateOnly(this.date) ?? undefined
  }

  constructor(intervention?: Omit<Intervention, 'dateAsDate'>) {
    this.id = intervention?.id
    this.date = intervention?.date
    this.completedBy = intervention?.completedBy
    this.completedByName = intervention?.completedByName
    this.createdByName = intervention?.createdByName
    this.type = intervention?.type
    this.subType = intervention?.subType
    this.crops = intervention?.crops ?? []
    this.region = intervention?.region
    this.regionId = intervention?.regionId ?? null
    this.note = intervention?.note
    this.picturesPath = intervention?.picturesPath ?? []
    this.disablingDate = intervention?.disablingDate ?? null
    if (intervention?.id) {
      this.id = intervention?.id
    }
    if (intervention?.pesticides) {
      this.pesticides = intervention?.pesticides
    }
    if (intervention?.irrigation) {
      this.irrigation = new InterventionIrrigation(intervention?.irrigation)
    }
    if (intervention?.mechanicalWeeding) {
      this.mechanicalWeeding = new InterventionMechanicalWeeding(intervention?.mechanicalWeeding)
    }
    if (intervention?.otherOperation) {
      this.otherOperation = new InterventionOtherOperation(intervention?.otherOperation)
    }
    if (intervention?.foreignMaterial) {
      this.foreignMaterial = new InterventionForeignMaterial(intervention?.foreignMaterial)
    }
    if (intervention?.foodSafety) {
      this.foodSafety = new InterventionFoodSafety(intervention?.foodSafety)
    }
    if (intervention?.nutrient) {
      this.nutrient = new InterventionNutrient(intervention?.nutrient)
    }

    if (intervention?.recommendation) {
      this.recommendation = intervention.recommendation
    }
    if (intervention?.workOrder) {
      this.workOrder = intervention.workOrder
    }
    this.externalId = intervention?.externalId
    this.externalClientId = intervention?.externalClientId
    this.documents = intervention?.documents ?? null
    this.validatedDate = intervention?.validatedDate ?? null
    this.validatedBy = intervention?.validatedBy ?? null
    this.validatedByFirstname = intervention?.validatedByFirstname ?? null
    this.validatedByLastname = intervention?.validatedByLastname ?? null
  }
}

export class InterventionPesticides {
  completedDate: string
  weatherConditions: string
  products: Product[]
  waterAmount: number
  waterUnit: string
  waterAreaUnit: string
  windSpeed?: number
  windSpeedUnit?: string
  applicationEquipment?: string
  area?: number
  areaUnit?: string
  windDirection?: string
  temperature?: number
  temperatureUnit?: string
  targetPest?: string
  supplierId?: number | null
  supplierIsFarm?: boolean | null
  supplierName?: string | null

  get completedDateAsDate(): Date | undefined {
    return parseDateOnly(this.completedDate) ?? undefined
  }

  constructor(interventionPesticides: Omit<InterventionPesticides, 'completedDateAsDate'>) {
    this.completedDate = interventionPesticides?.completedDate ?? ''
    this.weatherConditions = interventionPesticides?.weatherConditions ?? ''
    this.waterAmount = +(interventionPesticides?.waterAmount ?? 0)
    this.waterUnit = interventionPesticides?.waterUnit ?? ''
    this.waterAreaUnit = interventionPesticides?.waterAreaUnit ?? ''
    this.products = interventionPesticides?.products.map((p) => new Product(p))
    if (interventionPesticides?.windSpeed) {
      this.windSpeed = interventionPesticides.windSpeed
    }
    if (interventionPesticides?.windSpeedUnit) {
      this.windSpeedUnit = interventionPesticides.windSpeedUnit
    }
    if (interventionPesticides?.applicationEquipment) {
      this.applicationEquipment = interventionPesticides.applicationEquipment
    }
    this.area = interventionPesticides?.area ?? 0
    this.areaUnit = interventionPesticides?.areaUnit ?? ''
    this.windDirection = interventionPesticides?.windDirection ?? ''
    this.temperature = interventionPesticides?.temperature
    this.temperatureUnit = interventionPesticides?.temperatureUnit
    this.targetPest = interventionPesticides?.targetPest ?? ''
    this.supplierId = interventionPesticides?.supplierId ?? null
    this.supplierIsFarm = interventionPesticides?.supplierIsFarm ?? null
    this.supplierName = interventionPesticides?.supplierName ?? null
  }
}

export type InterventionId = Pick<Intervention, 'id'>
